import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { ApiError } from '../../models/api-error.enum';
import type { Album, AlbumAccessResponse, Track } from '../models/album.interface';
import { DEMO_ALBUM_ID, loadAlbum, loadAlbumByProductId, loadDemoAlbum } from './music.thunk';

type AlbumAccessState = Pick<AlbumAccessResponse, 'accessResponse' | 'availabilityResponse' | 'authenticated'>;

interface MusicState {
  currentAlbumId: string | null;
  currentTrackIndex: number;
  isPlaying: boolean;
  hasActivated: boolean;
  isShuffle: boolean;
  seekToStartAt: number;
  queueSource: 'album' | 'post';
  postQueue: Track[];
  albums: Record<string, Album>;
  albumsAccess: Record<string, AlbumAccessState>;
  albumsLoading: Record<string, boolean>;
  albumsError: Record<string, string | null>;
  /**
   * A release inside its visibility window answers 404 (SNGL-7177), which is not a failure to
   * report to a fan -- it means there is nothing to show. Tracked apart from `albumsError` so the
   * blocks can render nothing instead of an error banner, without changing what other errors say.
   */
  albumsNotFound: Record<string, boolean>;
  clientIp: string | null;
}

const initialState: MusicState = {
  currentAlbumId: null,
  currentTrackIndex: 0,
  isPlaying: false,
  hasActivated: false,
  isShuffle: false,
  seekToStartAt: 0,
  queueSource: 'album',
  postQueue: [],
  albums: {},
  albumsAccess: {},
  albumsLoading: {},
  albumsError: {},
  albumsNotFound: {},
  clientIp: null,
};

const musicSlice = createSlice({
  name: 'music',
  initialState,
  reducers: {
    setCurrentMusicAlbum: (state, action: PayloadAction<string | null>) => {
      if (state.currentAlbumId !== action.payload) {
        state.currentAlbumId = action.payload;
        // Browsing an album page must not interrupt an active post queue —
        // only an explicit album play (playTrack) reclaims the bar.
        if (state.queueSource !== 'post') {
          state.currentTrackIndex = 0;
          state.isPlaying = false;
        }
      }
    },
    setCurrentTrackIndex: (state, action: PayloadAction<number>) => {
      state.currentTrackIndex = action.payload;
    },
    setMusicIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
    },
    setMusicIsShuffling: (state, action: PayloadAction<boolean>) => {
      state.isShuffle = action.payload;
    },
    restartCurrentTrack: (state) => {
      state.seekToStartAt = Date.now();
      state.isPlaying = true;
    },
    /**
     * Re-attach to audio that survived a DOM teardown (the singleton engine
     * keeps playing across section re-renders). Restores player state without
     * the play-from-zero side effects of playTrack.
     */
    adoptPlayback: (
      state,
      action: PayloadAction<{ albumId: string; index: number; isPlaying: boolean }>
    ) => {
      state.currentAlbumId = action.payload.albumId;
      state.currentTrackIndex = action.payload.index;
      state.isPlaying = action.payload.isPlaying;
      state.hasActivated = true;
      state.queueSource = 'album';
    },
    playTrack: (state, action: PayloadAction<{ albumId?: string; index: number }>) => {
      const { albumId, index } = action.payload;
      if (albumId && state.currentAlbumId !== albumId) {
        state.currentAlbumId = albumId;
        state.currentTrackIndex = 0;
      }
      state.currentTrackIndex = index;
      state.isPlaying = true;
      state.hasActivated = true;
      state.queueSource = 'album';
    },
    /** Point the bar at a single post-audio track; it becomes the live queue. */
    playPostAudio: (state, action: PayloadAction<Track>) => {
      state.postQueue = [action.payload];
      state.queueSource = 'post';
      state.currentTrackIndex = 0;
      state.isPlaying = true;
      state.hasActivated = true;
    },
    /** Token refresh for the active post queue (playbackId guards staleness). */
    updatePostQueueToken: (
      state,
      action: PayloadAction<{ playbackId?: string; token?: string | null }>
    ) => {
      const track = state.postQueue[0];
      if (track && action.payload.playbackId && track.playbackId === action.payload.playbackId) {
        track.token = action.payload.token ?? null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAlbum.pending, (state, action) => {
        state.albumsLoading[action.meta.arg] = true;
        state.albumsError[action.meta.arg] = null;
        state.albumsNotFound[action.meta.arg] = false;
      })
      .addCase(loadAlbum.fulfilled, (state, action) => {
        state.albumsLoading[action.meta.arg] = false;
        state.albums[action.meta.arg] = action.payload.content;
        state.clientIp = action.payload.clientIp ?? state.clientIp;
        state.albumsAccess[action.meta.arg] = {
          accessResponse: action.payload.accessResponse,
          availabilityResponse: action.payload.availabilityResponse,
          authenticated: action.payload.authenticated,
        };
      })
      .addCase(loadAlbum.rejected, (state, action) => {
        state.albumsLoading[action.meta.arg] = false;
        state.albumsNotFound[action.meta.arg] = action.payload === ApiError.Not_Found;
        state.albumsError[action.meta.arg] = action.error.message ?? 'Failed to load album';
      })
      .addCase(loadAlbumByProductId.pending, (state, action) => {
        state.albumsLoading[action.meta.arg] = true;
        state.albumsError[action.meta.arg] = null;
        state.albumsNotFound[action.meta.arg] = false;
      })
      .addCase(loadAlbumByProductId.fulfilled, (state, action) => {
        state.albumsLoading[action.meta.arg] = false;
        state.albums[action.meta.arg] = action.payload.content;
        state.clientIp = action.payload.clientIp ?? state.clientIp;
        state.albumsAccess[action.meta.arg] = {
          accessResponse: action.payload.accessResponse,
          availabilityResponse: action.payload.availabilityResponse,
          authenticated: action.payload.authenticated,
        };
      })
      .addCase(loadAlbumByProductId.rejected, (state, action) => {
        state.albumsLoading[action.meta.arg] = false;
        state.albumsNotFound[action.meta.arg] = action.payload === ApiError.Not_Found;
        state.albumsError[action.meta.arg] = action.error.message ?? 'Failed to load album';
      })
      .addCase(loadDemoAlbum.pending, (state) => {
        state.albumsLoading[DEMO_ALBUM_ID] = true;
        state.albumsError[DEMO_ALBUM_ID] = null;
      })
      .addCase(loadDemoAlbum.fulfilled, (state, action) => {
        state.albumsLoading[DEMO_ALBUM_ID] = false;
        state.albums[DEMO_ALBUM_ID] = action.payload.content;
        state.albumsAccess[DEMO_ALBUM_ID] = {
          accessResponse: action.payload.accessResponse,
          availabilityResponse: action.payload.availabilityResponse,
          authenticated: action.payload.authenticated,
        };
      })
      .addCase(loadDemoAlbum.rejected, (state, action) => {
        state.albumsLoading[DEMO_ALBUM_ID] = false;
        state.albumsError[DEMO_ALBUM_ID] = action.error.message ?? 'Failed to load demo';
      });
  },
});

export const {
  setCurrentMusicAlbum,
  setCurrentTrackIndex,
  setMusicIsPlaying,
  setMusicIsShuffling,
  restartCurrentTrack,
  adoptPlayback,
  playTrack,
  playPostAudio,
  updatePostQueueToken,
} = musicSlice.actions;
export const musicReducer = musicSlice.reducer;
